
import React, { useEffect, useState } from 'react';
import { Card, Descriptions, List, Typography, Spin, Alert, Table, InputNumber, Button, Tag } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import api from 'Common/Client/Interceptor';
import moment from 'moment';
import { Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const { Title, Text } = Typography;

const PerformanceReviewReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { learnerId, firstName, lastName } = location.state || {};
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReview = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await api.get(
          `${process.env.REACT_APP_API_URL}/PerformanceEvaluation/Get-Previous-Evaluation?learnerId=${learnerId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.performanceReview) {
          setData(res.data.performanceReview);
        } else {
          setError('No evaluation data found in response');
        }
      } catch (err) {
        console.error('Fetch error:', err);
        setError(err.message || 'Failed to fetch performance review data');
      } finally {
        setLoading(false);
      }
    };

    if (learnerId) {
      fetchReview();
    } else {
      setError('No learner ID provided');
      setLoading(false);
    }
  }, [learnerId]);

  if (loading) return <Spin tip="Loading performance review..." />;
  if (error) return <Alert type="error" message={error} showIcon />;
  if (!data) return <Alert type="warning" message="No performance review data available" showIcon />;

  const pr = data;

  // Prepare data for radar chart
  const getRadarChartData = () => {
    if (!pr.performanceFactors) {
      console.error('No performance factors found');
      return null;
    }

    // Extract performance factors
    const {
      jobKnowledgeTechnicalSkillsRating,
      administrationRating,
      decisionMakingRating,
      communicationRating,
      initiativeResponsibilityRating,
      workRelationshipsRating,
      flexibilityRating,
      leadershipRating,
      freeThinkingRating
    } = pr.performanceFactors;

    // Extract skills from accountability goals
    const skillsData = pr.accountabilityGoals?.map(goal => ({
      name: goal.learnerSkillName || `Skill ${goal.learnerSkillId}`,
      value: goal.rating
    })) || [];

    // Combine performance factors and skills
    const performanceFactors = [
      { name: "Job Knowledge", value: jobKnowledgeTechnicalSkillsRating },
      { name: "Administration", value: administrationRating },
      { name: "Decision Making", value: decisionMakingRating },
      { name: "Communication", value: communicationRating },
      { name: "Initiative", value: initiativeResponsibilityRating },
      { name: "Work Relationships", value: workRelationshipsRating },
      { name: "Flexibility", value: flexibilityRating },
      { name: "Leadership", value: leadershipRating },
      { name: "Creativity", value: freeThinkingRating }
    ];

    return {
      performanceFactors,
      skills: skillsData
    };
  };

  const chartData = getRadarChartData();

  const radarOptions = {
    responsive: true,
    scales: {
      r: {
        angleLines: {
          display: true
        },
        suggestedMin: 1,
        suggestedMax: 5,
        ticks: {
          stepSize: 1,
          backdropColor: 'rgba(0, 0, 0, 0)'
        }
      }
    },
    plugins: {
      legend: {
        position: 'top'
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.dataset.label}: ${context.raw}`
        }
      }
    }
  };

  // Performance Evaluation Summary data
  const summaryData = [
    {
      key: '1',
      section: 'I',
      description: 'Overall Accountability/Goal Rating',
      percentage: '50%',
      score: pr.weightedAccountabilityScore || 0,
      maxScore: 50,
      ratingCode: pr.performanceRatingCode?.code ? 
        `${pr.performanceRatingCode.code} (${pr.performanceRatingCode.name})` : 
        'N/A'
    },
    {
      key: '2',
      section: 'II',
      description: 'Overall Performance Factors Rating',
      percentage: '40%',
      score: pr.weightedPerformanceFactorsScore || 0,
      maxScore: 40,
      ratingCode: pr.performanceRatingCode?.code ? 
        `${pr.performanceRatingCode.code} (${pr.performanceRatingCode.name})` : 
        'N/A'
    },
    {
      key: '3',
      section: 'III',
      description: 'Overall Dynamic Organization Rating',
      percentage: '10%',
      score: pr.weightedDynamicOrganizationScore || 0,
      maxScore: 10,
      ratingCode: pr.performanceRatingCode?.code ? 
        `${pr.performanceRatingCode.code} (${pr.performanceRatingCode.name})` : 
        'N/A'
    },
    {
      key: '4',
      section: 'TOTAL',
      description: 'TOTAL PERFORMANCE RATING',
      percentage: '100%',
      score: pr.totalPerformanceScore || 0,
      maxScore: 100,
      ratingCode: pr.performanceRatingCode ? 
        `${pr.performanceRatingCode.code} (${pr.performanceRatingCode.name})` : 
        'N/A'
    }
  ];

  const summaryColumns = [
    {
      title: 'Section',
      dataIndex: 'section',
      key: 'section',
      render: (text, record) => (
        <Text strong={record.key === '4'}>{text}</Text>
      )
    },
    {
      title: 'Section Description',
      dataIndex: 'description',
      key: 'description',
      render: (text, record) => (
        <Text strong={record.key === '4'}>{text}</Text>
      )
    },
    {
      title: 'Percentage of Total Rating',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (text, record) => (
        <Text strong={record.key === '4'}>{text}</Text>
      )
    },
    {
      title: 'Score',
      dataIndex: 'score',
      key: 'score',
      render: (score, record) => (
        <InputNumber 
          value={score} 
          min={0} 
          max={record.maxScore} 
          precision={2}
          disabled
          style={{ 
            width: '100%', 
            fontWeight: record.key === '4' ? 'bold' : 'normal' 
          }}
        />
      )
    },
    {
      title: 'Rating Code',
      dataIndex: 'ratingCode',
      key: 'ratingCode',
      render: (text, record) => (
        <Text strong={record.key === '4'}>{text}</Text>
      )
    }
  ];


   return (
    <Card 
      className="card-professional"
      title={`Performance Review Report for ${pr.firstName} ${pr.lastName}`}
      extra={<Button onClick={() => navigate(-1)}>Back</Button>}
    >
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="Employee">{pr.firstName} {pr.lastName}</Descriptions.Item>
        <Descriptions.Item label="Email">{pr.email}</Descriptions.Item>
        <Descriptions.Item label="Manager">{pr.managerFirstName} {pr.managerLastName}</Descriptions.Item>
      
        <Descriptions.Item label="Business Group">{pr.businessGroupName}</Descriptions.Item>
        <Descriptions.Item label="Field">{pr.businessGroupFieldName}</Descriptions.Item>
        <Descriptions.Item label="Hire Date">
          {moment(pr.hireDate).isValid() ? moment(pr.hireDate).format('MM/DD/YYYY') : 'N/A'}
        </Descriptions.Item>
      
        <Descriptions.Item label="Overall Score">{pr.totalPerformanceScore?.toFixed(2) || 'N/A'}</Descriptions.Item>
        <Descriptions.Item label="Rating Code">
          {pr.performanceRatingCode ? `${pr.performanceRatingCode.code} - ${pr.performanceRatingCode.name}` : 'N/A'}
        </Descriptions.Item>
      </Descriptions>

      {/* Performance Visualization Section */}
      <div style={{ marginTop: 24 }}>
        <Title level={4}>Performance Visualization</Title>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24 }}>
          {chartData && (
            <>
              <div style={{ flex: 1, minWidth: 300, maxWidth: 500 }}>
                <Title level={5} style={{ textAlign: 'center' }}>Performance Factors</Title>
                <Radar 
                  data={{
                    labels: chartData.performanceFactors.map(item => item.name),
                    datasets: [{
                      label: 'Performance Factors Rating',
                      data: chartData.performanceFactors.map(item => item.value),
                      backgroundColor: 'rgba(54, 162, 235, 0.2)',
                      borderColor: 'rgba(54, 162, 235, 1)',
                      borderWidth: 2
                    }]
                  }} 
                  options={radarOptions}
                />
              </div>
              
              {chartData.skills.length > 0 && (
                <div style={{ flex: 1, minWidth: 300, maxWidth: 500 }}>
                  <Title level={5} style={{ textAlign: 'center' }}>Skills Assessment</Title>
                  <Radar 
                    data={{
                      labels: chartData.skills.map(item => item.name),
                      datasets: [{
                        label: 'Skills Rating',
                        data: chartData.skills.map(item => item.value),
                        backgroundColor: 'rgba(75, 192, 192, 0.2)',
                        borderColor: 'rgba(75, 192, 192, 1)',
                        borderWidth: 2
                      }]
                    }} 
                    options={radarOptions}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Title level={4} style={{ marginTop: 24 }}>I. Accountability Goals</Title>
      <List
        bordered
        dataSource={pr.accountabilityGoals || []}
        renderItem={(goal, index) => (
          <List.Item style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9' }}>
            <List.Item.Meta
              title={
                <span style={{ fontWeight: 500 }}>
                  Goal {goal.goalNumber}: {goal.learnerSkillName}
                  {goal.learnerSkillId && (
                    <Tag style={{ marginLeft: 8 }}>Skill: {goal.learnerSkillName || `ID: ${goal.learnerSkillId}`}</Tag>
                  )}
                </span>
              }
              description={
                <>
                  <p><strong>Weight:</strong> {goal.weight}%</p>
                  <p><strong>Rating:</strong> {goal.rating}</p>
                  <p><strong>Summary:</strong> {goal.summaryOfResults}</p>
                </>
              }
            />
          </List.Item>
        )}
      />

      {pr.identifyAdditionalGoals && (
        <>
          <Title level={4} style={{ marginTop: 24 }}>Additional Goals</Title>
          <div style={{ 
            border: "1px solid #d9d9d9",
            padding: "16px",
            borderRadius: "6px",
            backgroundColor: "#fafafa"
          }}>
            <p>{pr.identifyAdditionalGoals}</p>
          </div>
        </>
      )}

      <Title level={4} style={{ marginTop: 24 }}>II. Performance Factors</Title>
      <List
        bordered
        dataSource={[
          {
            title: "Job Knowledge / Technical Skills",
            rating: pr.performanceFactors?.jobKnowledgeTechnicalSkillsRating,
            evidence: pr.performanceFactors?.jobKnowledgeTechnicalSkillsEvidence,
          },
          {
            title: "Administration",
            rating: pr.performanceFactors?.administrationRating,
            evidence: pr.performanceFactors?.administrationEvidence,
          },
          {
            title: "Decision Making",
            rating: pr.performanceFactors?.decisionMakingRating,
            evidence: pr.performanceFactors?.decisionMakingEvidence,
          },
          {
            title: "Communication",
            rating: pr.performanceFactors?.communicationRating,
            evidence: pr.performanceFactors?.communicationEvidence,
          },
          {
            title: "Initiative / Responsibility",
            rating: pr.performanceFactors?.initiativeResponsibilityRating,
            evidence: pr.performanceFactors?.initiativeResponsibilityEvidence,
          },
          {
            title: "Work Relationships",
            rating: pr.performanceFactors?.workRelationshipsRating,
            evidence: pr.performanceFactors?.workRelationshipsEvidence,
          },
          {
            title: "Flexibility",
            rating: pr.performanceFactors?.flexibilityRating,
            evidence: pr.performanceFactors?.flexibilityEvidence,
          },
          {
            title: "Leadership",
            rating: pr.performanceFactors?.leadershipRating,
            evidence: pr.performanceFactors?.leadershipEvidence,
          },
          {
            title: "Free Thinking / Creativity",
            rating: pr.performanceFactors?.freeThinkingRating,
            evidence: pr.performanceFactors?.freeThinkingEvidence,
          }
        ]}
        renderItem={(item, index) => (
          <List.Item style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9f9f9' }}>
            <List.Item.Meta
              title={<span style={{ fontWeight: 500 }}>{item.title}</span>}
              description={
                <>
                  <p><strong>Rating:</strong> {item.rating ?? "N/A"}</p>
                  {item.evidence && <p><strong>Evidence:</strong> {item.evidence}</p>}
                </>
              }
            />
          </List.Item>
        )}
      />

      <Title level={4} style={{ marginTop: 24 }}>III. Dynamic Organization Evaluation</Title>
      <List
        bordered
        dataSource={[
          {
            title: "Demonstrating Behaviors Rating",
            value: pr.dynamicOrganizationEvaluation?.demonstratingBehaviorsRating ?? "N/A",
          },
          {
            title: "Valuing Diversity Rating",
            value: pr.dynamicOrganizationEvaluation?.valuingDiversityRating ?? "N/A",
          },
          {
            title: "Examples of Behaviors",
            value: pr.dynamicOrganizationEvaluation?.examplesOfBehaviors || "No examples provided",
          },
        ]}
        renderItem={(item, index) => (
          <List.Item style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9f9f9" }}>
            <List.Item.Meta
              title={<span style={{ fontWeight: 500 }}>{item.title}</span>}
              description={<p>{item.value}</p>}
            />
          </List.Item>
        )}
      />

      {/* Performance Evaluation Summary Section */}
      <div style={{
        border: "1px solid #d9d9d9",
        padding: "16px",
        margin: "24px 0",
        borderRadius: "6px",
        backgroundColor: "#fafafa"
      }}>
        <Title level={4} style={{ marginBottom: 16 }}>IV. Performance Evaluation Summary</Title>
        <Table
          columns={summaryColumns}
          dataSource={summaryData}
          pagination={false}
          bordered
          size="small"
          rowClassName={(record) => record.key === '4' ? 'summary-total-row' : ''}
        />
      </div>

      <Title level={4} style={{ marginTop: 24 }}>V. Summary of Strengths and Development Needs</Title>
      <List
        bordered
        dataSource={[
          {
            title: "A. Associate Strengths",
            value: pr.strengths || "No strengths provided",
          },
          {
            title: "B. Development Needs",
            value: pr.developmentNeeds || "No development needs provided",
          },
        ]}
        renderItem={(item, index) => (
          <List.Item style={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f9f9f9" }}>
            <List.Item.Meta
              title={<span style={{ fontWeight: 500 }}>{item.title}</span>}
              description={<p>{item.value}</p>}
            />
          </List.Item>
        )}
      />

      <Title level={4} style={{ marginTop: 24 }}>Action Plans</Title>
      <List
        bordered
        dataSource={pr.actionPlans || []}
        renderItem={(item, idx) => (
          <List.Item>{idx + 1}. {item}</List.Item>
        )}
      />

      <Title level={4} style={{ marginTop: 24 }}>Employee Comments</Title>
      <div style={{ 
        border: "1px solid #d9d9d9",
        padding: "16px",
        borderRadius: "6px",
        backgroundColor: "#fafafa"
      }}>
        <p>{pr.employeeComments || "No comments provided"}</p>
      </div>

    
    </Card>
  );
};

export default PerformanceReviewReport;